import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import { toast } from "sonner";

export function formatDataBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export function formatDataDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

/**
 * Salud, respaldo y restauración operativos (sin mantenimiento preventivo técnico).
 * @param {{ enabled?: boolean }} [options]
 */
export function useOperationalDataManagement({ enabled = true } = {}) {
  const isPlatformDesigner = usePlatformDesigner();
  const [health, setHealth] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [busyBackup, setBusyBackup] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [busyRestore, setBusyRestore] = useState(false);
  const [maintenanceJob, setMaintenanceJob] = useState(null);
  const [maintProgress, setMaintProgress] = useState(0);
  const [maintMessage, setMaintMessage] = useState("");
  const [securityKeys, setSecurityKeys] = useState(null);
  const pollRef = useRef(null);

  const modules = health?.modules || {};

  const load = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const healthReq = api.get("/system/data-management/health");
      const backupsReq = api.get("/system/data-management/backups");
      if (isPlatformDesigner) {
        const [hRes, bRes, skRes] = await Promise.all([
          healthReq,
          backupsReq,
          api.get("/system/security-keys").catch(() => ({ data: null })),
        ]);
        setHealth(hRes.data);
        setBackups(bRes.data?.items || []);
        setSecurityKeys(skRes.data);
      } else {
        const [hRes, bRes] = await Promise.all([healthReq, backupsReq]);
        setHealth(hRes.data);
        setBackups(bRes.data?.items || []);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar los datos del sistema");
    } finally {
      setLoading(false);
    }
  }, [enabled, isPlatformDesigner]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(function operationalDataPollCleanup() {
    function clearPollTimer() {
      const timerId = pollRef.current;
      if (timerId != null) {
        window.clearInterval(timerId);
        pollRef.current = null;
      }
    }
    return clearPollTimer;
  }, []);

  const pollMaintenance = (jobId) => {
    if (pollRef.current) window.clearInterval(pollRef.current);
    pollRef.current = window.setInterval(async () => {
      try {
        const { data } = await api.get(`/system/data-management/maintenance/${jobId}`);
        setMaintProgress(data.progress || 0);
        setMaintMessage(data.message || "");
        if (data.status === "completed" || data.status === "failed") {
          window.clearInterval(pollRef.current);
          pollRef.current = null;
          setMaintenanceJob(null);
          if (data.status === "completed") {
            toast.success("Mantenimiento completado (snapshot + limpieza)");
            load();
          } else {
            toast.error(data.error || "Error en mantenimiento");
          }
        }
      } catch {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
        setMaintenanceJob(null);
      }
    }, 800);
  };

  const createBackup = async () => {
    setBusyBackup(true);
    try {
      const { data } = await api.post("/system/data-management/backups");
      toast.success(`Respaldo creado: ${data?.backup?.filename || "respaldo"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo crear el respaldo");
    } finally {
      setBusyBackup(false);
    }
  };

  const confirmRestore = async () => {
    if (!restoreTarget) return;
    setBusyRestore(true);
    try {
      const { data } = await api.post(
        `/system/data-management/backups/${encodeURIComponent(restoreTarget.id)}/restore`,
        null,
        { params: { replace_existing: true }, timeout: 600000 },
      );
      const r = data?.restored || {};
      toast.success(`Restauración completada · Clientes: ${r.clients ?? 0} · CFDI: ${r.cfdis ?? 0}`);
      setRestoreTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo restaurar");
    } finally {
      setBusyRestore(false);
    }
  };

  const runMaintenance = async () => {
    if (
      !window.confirm(
        "Se generará un snapshot automático y luego se ejecutará limpieza (logs >30 días, archivos huérfanos). ¿Continuar?",
      )
    ) {
      return;
    }
    try {
      const { data } = await api.post("/system/data-management/maintenance");
      setMaintenanceJob(data.job_id);
      setMaintProgress(5);
      setMaintMessage("Iniciando…");
      pollMaintenance(data.job_id);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (e.response?.status === 409 && code === "SAT_DOWNLOAD_ACTIVE") {
        toast.error(
          detail?.message ||
            "Mantenimiento bloqueado: hay una descarga SAT (FIEL/CIEC) en curso. Espere a que termine.",
        );
      } else {
        toast.error(formatApiError(detail) || "No se pudo iniciar mantenimiento");
      }
    }
  };

  return {
    isPlatformDesigner,
    health,
    backups,
    modules,
    loading,
    busyBackup,
    restoreTarget,
    setRestoreTarget,
    busyRestore,
    maintenanceJob,
    maintProgress,
    maintMessage,
    securityKeys,
    load,
    createBackup,
    confirmRestore,
    runMaintenance,
  };
}
